import { useState } from "react";
import { taskService } from "@miplatform/api";
import {
  EMAIL_TEMPLATE_TYPE_TO_LABEL_MAP,
  HTTP_STATUSES,
} from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { IEmailTemplateResponse, ISuccessFailOptions } from "@miplatform/types";
import {
  IEmailTemplateData,
  IEmailTemplateFormItem,
} from "../../views/Config/OrderTemplates/ConfigOrderTemplates.types";

const EMAIL_TEMPLATES_TTL_MS = 2 * 60 * 1000;

const emailTemplatesCache = new Map<
  string,
  { fetchedAt: number; data: IEmailTemplateFormItem[] }
>();

export const useEmailTemplate = () => {
  const stateManager = useComponentState();

  const [emailTemplateDetails, setEmailTemplateDetails] = useState<
    IEmailTemplateFormItem[] | null
  >(null);

  const getEmailTemplatesByOrderTemplateId = (
    id: string,
    { onSuccess }: ISuccessFailOptions<IEmailTemplateFormItem[]> = {}
  ) => {
    const cached = emailTemplatesCache.get(id);
    if (cached && Date.now() - cached.fetchedAt < EMAIL_TEMPLATES_TTL_MS) {
      setEmailTemplateDetails(cached.data);
      onSuccess?.([...cached.data]);
      return;
    }

    stateManager.run(async () => {
      const response = await taskService.orderTemplates.configEmail.getById({
        orderId: id,
      });

      if (response.status === HTTP_STATUSES.OK) {
        const sortedEmailTemplates = response.data
          .map((template: IEmailTemplateResponse) => ({
            ...template,
            label:
              EMAIL_TEMPLATE_TYPE_TO_LABEL_MAP[
                template.emailType as keyof typeof EMAIL_TEMPLATE_TYPE_TO_LABEL_MAP
              ] || "",
          }))
          .sort((a: { label: string }, b: { label: string }) =>
            a.label.localeCompare(b.label)
          );
        emailTemplatesCache.set(id, {
          fetchedAt: Date.now(),
          data: sortedEmailTemplates,
        });

        setEmailTemplateDetails(sortedEmailTemplates);
        onSuccess?.([...sortedEmailTemplates]);
      }

      return response;
    });
  };

  const updateEmailTemplates = async (
    data: IEmailTemplateData,
    id: string,
    { onSuccess }: ISuccessFailOptions<IEmailTemplateFormItem[]> = {}
  ) => {
    emailTemplatesCache.clear();
    const filteredEmailData = {
      ...data,
      emailTemplates: data.emailTemplates
        .filter((template) => template.emailType !== "")
        .map((template) => ({
          ...template,
          to: template.to?.filter((recipient) => recipient.type),
          cc: template.cc?.filter((recipient) => recipient.type),
        })),
    };
    return stateManager.run(async () => {
      const response = await taskService.orderTemplates.configEmail.update({
        orderId: id,
        data: filteredEmailData.emailTemplates,
        toastMessageConfig: {
          success: {
            text: `Email Template was updated successfully.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        getEmailTemplatesByOrderTemplateId(id, { onSuccess });
      }
      return response;
    });
  };

  return {
    isLoading: stateManager.isBusy(),
    emailTemplateDetails,
    getEmailTemplatesByOrderTemplateId,
    updateEmailTemplates,
  };
};
